import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { VIP_IMG } from "@/lib/media";
import ProductCard from "@/components/ProductCard";
import Reveal, { Kicker } from "@/components/Reveal";
import Seo from "@/components/Seo";

export default function Tickets() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products", { params: { kind: "ticket" } })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="tickets-page" className="pt-28 md:pt-36 pb-24">
      <Seo path="/tickets" title="Tickets & Preise zur Krampus-Show 2026 | Latschenwirt" titleEn="Tickets & Prices – Krampus Show 2026 | Latschenwirt" description="Arena-, VIP- und Tisch-Angebote für die Krampus-Show am 20. & 21. November 2026 beim Latschenwirt in Großgmain bei Salzburg. Preise, Kategorien und Buchung." descriptionEn="Arena, VIP and table packages for the Krampus Show on 20 & 21 November 2026 at the Latschenwirt in Grossgmain near Salzburg. Prices, categories and booking." />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="mb-16 text-center">
          <Kicker>{t("tickets_kicker")}</Kicker>
          <h1 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-6 ember-glow">{t("tickets_hero_t")}</h1>
          <p className="text-hell-smoke max-w-2xl md:mx-auto text-base md:text-lg">{t("tickets_hero_d")}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="tickets-date-banner"
            className="flex items-center gap-3 border border-hell-ember/40 bg-hell-red/15 px-6 py-4 mb-12">
            <Flame className="w-5 h-5 text-hell-ember shrink-0 animate-flicker" />
            <p className="font-cinzel text-xs md:text-sm tracking-[0.2em] uppercase text-hell-bone">{t("tickets_banner")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div data-testid="tickets-event-links" className="flex flex-wrap justify-center gap-3 mb-10">
            <Link to={`${lang === "en" ? "/en" : ""}/krampus-show-2026-11-20`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-5 py-2.5 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {lang === "de" ? "Termin Freitag, 20.11." : "Friday, 20 Nov"}
            </Link>
            <Link to={`${lang === "en" ? "/en" : ""}/krampus-show-2026-11-21`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-5 py-2.5 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {lang === "de" ? "Termin Samstag, 21.11." : "Saturday, 21 Nov"}
            </Link>
            <Link to={`${lang === "en" ? "/en" : ""}/anreise`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-5 py-2.5 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {lang === "de" ? "Anreise & Parken" : "Getting there & parking"}
            </Link>
          </div>
        </Reveal>
        {loading ? (
          <p className="text-hell-smoke font-cinzel tracking-widest">…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
        <Reveal className="mt-20 border border-white/10 bg-hell-coal p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <Kicker>VIP</Kicker>
            <h2 className="font-display text-3xl md:text-4xl text-hell-bone mt-3 mb-4">Latschenwirt-Stuben</h2>
            <p className="text-hell-smoke leading-relaxed">
              {t("ticket_vip_hint") !== "ticket_vip_hint"
                ? t("ticket_vip_hint")
                : "Exklusive VIP-Tische in den Stuben – nur noch wenige verfügbar. Inklusive Kulinarik, Fackelschein und bestem Blick auf die Nacht."}
            </p>
          </div>
          <img src={VIP_IMG} alt="Latschenwirt Stuben" className="w-full h-64 object-cover border border-white/10" />
        </Reveal>
      </div>
    </main>
  );
}
